/**
 * Demo Data — Supervision only
 *
 * Seed data for live calls used when demo mode is active in the supervision console.
 * Contains 25 realistic concurrent live calls (the maximum supervision wall capacity).
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────
const ago = (minutesAgo: number) =>
  new Date(Date.now() - minutesAgo * 60_000).toISOString();

// ─── Supervision (Live Monitored Calls) ───────────────────────────────────────
export const DEMO_SUPERVISED_CALLS = [
  {
    call_id: "live_sup_01",
    contact_name: "Rahul Sharma",
    customer_name: "Rahul Sharma",
    customer_phone: "+91 98001 23456",
    agent_id: "agent_demo_01",
    agent_name: "Priya (Hindi Customer Support)",
    status: "in_progress",
    created_at: ago(3),
    last_message_at: ago(0.2),
    severity: "info" as const,
    severity_open: "info" as const,
    risk: { score: 15, level: "green" as const },
    alerts: { open: 0, acknowledged: 0, overdue: 0, total: 0, top: null },
    recent: [
      { role: "assistant" as const, text: "नमस्ते राहुल जी, TechLadder AI में आपका स्वागत है। क्या मैं आपकी सहायता कर सकती हूँ?", created_at: ago(2) },
      { role: "user" as const, text: "हाँ, मुझे आपके आउटबाउंड वॉयस एजेंट्स के प्राइसिंग प्लान्स जानने हैं।", created_at: ago(1.5) },
      { role: "assistant" as const, text: "ज़रूर! हमारा बिज़नेस प्लान ₹12,999 प्रति माह से शुरू होता है जिसमें 20,000 मिनट्स शामिल हैं। क्या आप कस्टम वॉयस क्लोनिंग भी चाहते हैं?", created_at: ago(0.5) },
      { role: "user" as const, text: "हाँ, हमारी कंपनी के लिए हमें हिंदी और इंग्लिश दोनों आवाज़ें चाहिए।", created_at: ago(0.1) },
    ],
  },
  {
    call_id: "live_sup_02",
    contact_name: "Vikram Patel",
    customer_name: "Vikram Patel",
    customer_phone: "+91 98765 43210",
    agent_id: "agent_demo_02",
    agent_name: "Alex (Outbound Sales Specialist)",
    status: "in_progress",
    created_at: ago(5),
    last_message_at: ago(0.4),
    severity: "warn" as const,
    severity_open: "warn" as const,
    risk: { score: 45, level: "amber" as const },
    alerts: {
      open: 1, acknowledged: 0, overdue: 0, total: 1,
      top: {
        alert_id: "alert_01",
        type: "competitor_mention",
        severity: "warn" as const,
        status: "open" as const,
        detail: "Customer mentioned competing solution (Exotel / Bland AI)",
        quoted_text: "We are currently evaluating Bland AI and wanted to compare your API response times.",
        first_seen_at: ago(1),
      },
    },
    recent: [
      { role: "user" as const, text: "We are currently evaluating Bland AI and wanted to compare your API response times.", created_at: ago(1) },
      { role: "assistant" as const, text: "That is a great comparison. TechLadder has sub-450ms turnaround latency directly from Mumbai servers, avoiding overseas routing latency.", created_at: ago(0.4) },
    ],
  },
  {
    call_id: "live_sup_03",
    contact_name: "Anjali Singh",
    customer_name: "Anjali Singh",
    customer_phone: "+91 97654 32109",
    agent_id: "agent_demo_04",
    agent_name: "Sam (Enterprise Renewal Advisor)",
    status: "in_progress",
    created_at: ago(7),
    last_message_at: ago(0.8),
    severity: "info" as const,
    severity_open: null,
    risk: { score: 10, level: "green" as const },
    alerts: { open: 0, acknowledged: 0, overdue: 0, total: 0, top: null },
    recent: [
      { role: "assistant" as const, text: "Good afternoon Anjali, calling from TechLadder to verify if your contract renewal documents were received?", created_at: ago(4) },
      { role: "user" as const, text: "Yes Sam, our finance director signed the contract this morning. Can you email the counter-signed copy?", created_at: ago(2) },
      { role: "assistant" as const, text: "Sending it over immediately to your registered corporate email right now.", created_at: ago(0.8) },
    ],
  },
  {
    call_id: "live_sup_04",
    contact_name: "Amit Verma",
    customer_name: "Amit Verma",
    customer_phone: "+91 98111 22334",
    agent_id: "agent_demo_03",
    agent_name: "Riya (Hinglish Concierge)",
    status: "in_progress",
    created_at: ago(2.5),
    last_message_at: ago(0.3),
    severity: "warn" as const,
    severity_open: "warn" as const,
    risk: { score: 55, level: "amber" as const },
    alerts: {
      open: 1, acknowledged: 0, overdue: 0, total: 1,
      top: {
        alert_id: "alert_04",
        type: "payment_dispute",
        severity: "warn" as const,
        status: "open" as const,
        detail: "Customer disputing invoice charges",
        quoted_text: "Maine extra credits ke liye pay nahi kiya tha, bill me extra amount kyu hai?",
        first_seen_at: ago(1.2),
      },
    },
    recent: [
      { role: "user" as const, text: "Maine extra credits ke liye pay nahi kiya tha, bill me extra amount kyu hai?", created_at: ago(1.2) },
      { role: "assistant" as const, text: "Amit ji, main abhi aapka billing ledger check kar rahi hoon. Ek minute dijiye please.", created_at: ago(0.3) },
    ],
  },
  {
    call_id: "live_sup_05",
    contact_name: "Neha Gupta",
    customer_name: "Neha Gupta",
    customer_phone: "+91 99223 34455",
    agent_id: "agent_demo_01",
    agent_name: "Priya (Hindi Customer Support)",
    status: "in_progress",
    created_at: ago(4.1),
    last_message_at: ago(0.5),
    severity: "info" as const,
    severity_open: "info" as const,
    risk: { score: 20, level: "green" as const },
    alerts: { open: 0, acknowledged: 0, overdue: 0, total: 0, top: null },
    recent: [
      { role: "assistant" as const, text: "नमस्ते नेहा जी, क्या आपका व्हाट्सएप इंटीग्रेशन सफलतापूर्वक एक्टिवेट हो गया?", created_at: ago(2.5) },
      { role: "user" as const, text: "हाँ, QR कोड स्कैन करने के बाद अब ऑटो-रिप्लाई सही से जा रहे हैं। धन्यवाद!", created_at: ago(0.5) },
    ],
  },
  {
    call_id: "live_sup_06",
    contact_name: "Rohan Mehta",
    customer_name: "Rohan Mehta",
    customer_phone: "+91 98334 45566",
    agent_id: "agent_demo_02",
    agent_name: "Alex (Outbound Sales Specialist)",
    status: "in_progress",
    created_at: ago(6.2),
    last_message_at: ago(0.1),
    severity: "critical" as const,
    severity_open: "critical" as const,
    risk: { score: 85, level: "red" as const },
    alerts: {
      open: 1, acknowledged: 0, overdue: 1, total: 1,
      top: {
        alert_id: "alert_06",
        type: "supervisor_escalation",
        severity: "critical" as const,
        status: "open" as const,
        detail: "Customer demanding live human supervisor immediately",
        quoted_text: "I want to speak with your manager right now, stop using this AI bot.",
        first_seen_at: ago(2),
      },
    },
    recent: [
      { role: "user" as const, text: "I want to speak with your manager right now, stop using this AI bot.", created_at: ago(2) },
      { role: "assistant" as const, text: "I completely understand Rohan. I am immediately alerting my floor supervisor to take over this call.", created_at: ago(0.1) },
    ],
  },
  {
    call_id: "live_sup_07",
    contact_name: "Priya Iyer",
    customer_name: "Priya Iyer",
    customer_phone: "+91 98445 56677",
    agent_id: "agent_demo_04",
    agent_name: "Sam (Enterprise Renewal Advisor)",
    status: "in_progress",
    created_at: ago(3.8),
    last_message_at: ago(0.6),
    severity: "info" as const,
    severity_open: null,
    risk: { score: 12, level: "green" as const },
    alerts: { open: 0, acknowledged: 0, overdue: 0, total: 0, top: null },
    recent: [
      { role: "assistant" as const, text: "Hello Priya, verifying your dedicated SIP Trunk allocation for next quarter.", created_at: ago(2) },
      { role: "user" as const, text: "We need 100 concurrent channels instead of 50 starting next Monday.", created_at: ago(0.6) },
    ],
  },
  {
    call_id: "live_sup_08",
    contact_name: "Karan Malhotra",
    customer_name: "Karan Malhotra",
    customer_phone: "+91 98556 67788",
    agent_id: "agent_demo_03",
    agent_name: "Riya (Hinglish Concierge)",
    status: "in_progress",
    created_at: ago(1.9),
    last_message_at: ago(0.2),
    severity: "info" as const,
    severity_open: "info" as const,
    risk: { score: 18, level: "green" as const },
    alerts: { open: 0, acknowledged: 0, overdue: 0, total: 0, top: null },
    recent: [
      { role: "assistant" as const, text: "Hi Karan! Looking to schedule your platform onboarding demo today?", created_at: ago(1) },
      { role: "user" as const, text: "Yes please, 4 PM IST works best for our engineering team.", created_at: ago(0.2) },
    ],
  },
  {
    call_id: "live_sup_09",
    contact_name: "Pooja Nair",
    customer_name: "Pooja Nair",
    customer_phone: "+91 98667 78899",
    agent_id: "agent_demo_01",
    agent_name: "Priya (Hindi Customer Support)",
    status: "in_progress",
    created_at: ago(5.4),
    last_message_at: ago(0.4),
    severity: "warn" as const,
    severity_open: "warn" as const,
    risk: { score: 40, level: "amber" as const },
    alerts: {
      open: 1, acknowledged: 0, overdue: 0, total: 1,
      top: {
        alert_id: "alert_09",
        type: "long_silence",
        severity: "warn" as const,
        status: "open" as const,
        detail: "Caller silent for over 15 seconds after prompt",
        first_seen_at: ago(0.8),
      },
    },
    recent: [
      { role: "assistant" as const, text: "पूजा जी, क्या आप मुझे सुन पा रही हैं?", created_at: ago(0.4) },
    ],
  },
].map((c) => ({ ...c, is_demo: true as const }));

